import { Controller } from '@hotwired/stimulus'

const THEMES = ['light', 'dark']
const STORAGE_KEY = 'theme'
const THEME_TRANSITION_CLASS = 'theme-transitioning'
const THEME_TRANSITION_DURATION = 160

export default class extends Controller {
  static targets = ['input']

  connect() {
    this.boundHandleSystemChange = this.#handleSystemChange.bind(this)
    this.systemQuery = window.matchMedia('(prefers-color-scheme: dark)')
    this.reducedMotionQuery = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    )
    this.systemQuery.addEventListener('change', this.boundHandleSystemChange)
    this.#applyTheme(this.#getPreferredTheme())
  }

  disconnect() {
    clearTimeout(this.transitionTimeout)

    this.systemQuery?.removeEventListener(
      'change',
      this.boundHandleSystemChange
    )
  }

  toggle(event) {
    const newTheme = event.target.checked ? 'dark' : 'light'
    localStorage.setItem(STORAGE_KEY, newTheme)
    this.#applyTheme(newTheme, { animated: true })
  }

  #applyTheme(theme, { animated = false } = {}) {
    if (animated && this.#canUseViewTransition()) {
      document.startViewTransition(() => this.#renderTheme(theme))
      return
    }

    if (animated) this.#startThemeTransition()
    this.#renderTheme(theme)
    if (animated) this.#finishThemeTransitionAfterDelay()
  }

  #canUseViewTransition() {
    return (
      typeof document.startViewTransition === 'function' &&
      !this.reducedMotionQuery?.matches
    )
  }

  #finishThemeTransitionAfterDelay() {
    this.transitionTimeout = setTimeout(() => {
      document.documentElement.classList.remove(THEME_TRANSITION_CLASS)
    }, THEME_TRANSITION_DURATION)
  }

  #getPreferredTheme() {
    return (
      this.#getStoredTheme() || (this.systemQuery.matches ? 'dark' : 'light')
    )
  }

  #getStoredTheme() {
    const value = localStorage.getItem(STORAGE_KEY)
    if (THEMES.includes(value)) return value

    localStorage.removeItem(STORAGE_KEY)
  }

  #handleSystemChange() {
    if (this.#getStoredTheme()) return

    this.#applyTheme(this.#getPreferredTheme(), { animated: true })
  }

  #renderTheme(theme) {
    const isDark = theme === 'dark'
    document.documentElement.classList.toggle('dark', isDark)
    this.inputTarget.checked = isDark
  }

  #startThemeTransition() {
    clearTimeout(this.transitionTimeout)
    document.documentElement.classList.add(THEME_TRANSITION_CLASS)
  }
}
